import React, { useRef, useEffect } from 'react';
import { useI18n } from '../../i18n';
import './ArabicKeyboard.css';

const ARABIC_KEYBOARD_LAYOUT = [
  ['ض', 'ص', 'ث', 'ق', 'ف', 'غ', 'ع', 'ه', 'خ', 'ح', 'ج', 'د'],
  ['ش', 'س', 'ي', 'ب', 'ل', 'ا', 'ت', 'ن', 'م', 'ك', 'ط'],
  ['ئ', 'ء', 'ؤ', 'ر', 'لا', 'ى', 'ة', 'و', 'ز', 'ظ'],
  ['ذ', 'أ', 'إ', 'آ', 'ـ', 'ً', 'ٌ', 'ٍ', 'َ', 'ُ', 'ِ', 'ّ']
];

const ArabicKeyboard = ({ value, onChange, onClose }) => {
  const { t } = useI18n();
  const inputRef = useRef(null);

  // Focus input on mount
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
      // Place cursor at end
      inputRef.current.setSelectionRange(value.length, value.length);
    }
  }, []);

  const insertAtCursor = (char) => {
    const input = inputRef.current;
    if (!input) {
      onChange(value + char);
      return;
    }

    const start = input.selectionStart;
    const end = input.selectionEnd;
    const newValue = value.slice(0, start) + char + value.slice(end);
    onChange(newValue);

    // Restore cursor position after React updates
    setTimeout(() => {
      const newPos = start + char.length;
      input.setSelectionRange(newPos, newPos);
      input.focus();
    }, 0);
  };

  const handleBackspace = () => {
    const input = inputRef.current;
    if (!input) {
      onChange(value.slice(0, -1));
      return;
    }

    const start = input.selectionStart;
    const end = input.selectionEnd;

    if (start !== end) {
      // Delete selection
      const newValue = value.slice(0, start) + value.slice(end);
      onChange(newValue);
      setTimeout(() => {
        input.setSelectionRange(start, start);
        input.focus();
      }, 0);
    } else if (start > 0) {
      // Delete character before cursor
      const newValue = value.slice(0, start - 1) + value.slice(start);
      onChange(newValue);
      setTimeout(() => {
        input.setSelectionRange(start - 1, start - 1);
        input.focus();
      }, 0);
    }
  };

  const handleClear = () => {
    onChange('');
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }, 0);
  };

  const handleSpace = () => {
    insertAtCursor(' ');
  };

  const handleKeyClick = (char) => {
    insertAtCursor(char);
  };

  return (
    <div className="arabic-keyboard">
      <div className="keyboard-header">
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="keyboard-input"
          dir="rtl"
        />
        <button className="keyboard-close tooltip-trigger" onClick={onClose} data-tooltip={t('Close and save')} aria-label={t('Close and save')}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="20 6 9 17 4 12" />
          </svg>
        </button>
      </div>
      
      <div className="keyboard-rows">
        {ARABIC_KEYBOARD_LAYOUT.map((row, rowIndex) => (
          <div key={rowIndex} className="keyboard-row">
            {row.map((char, charIndex) => (
              <button
                key={charIndex}
                className="keyboard-key"
                onClick={() => handleKeyClick(char)}
                onMouseDown={(e) => e.preventDefault()} // Prevent focus loss
              >
                {char}
              </button>
            ))}
          </div>
        ))}
        
        {/* Bottom row with special keys */}
        <div className="keyboard-row keyboard-row-special">
          <button 
            className="keyboard-key key-clear" 
            onClick={handleClear}
            onMouseDown={(e) => e.preventDefault()}
          >
            مسح
          </button>
          <button 
            className="keyboard-key key-space" 
            onClick={handleSpace}
            onMouseDown={(e) => e.preventDefault()}
          >
            مسافة
          </button>
          <button 
            className="keyboard-key key-backspace" 
            onClick={handleBackspace}
            onMouseDown={(e) => e.preventDefault()}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 4H8l-7 8 7 8h13a2 2 0 002-2V6a2 2 0 00-2-2z" />
              <line x1="18" y1="9" x2="12" y2="15" />
              <line x1="12" y1="9" x2="18" y2="15" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};

export default ArabicKeyboard;
